import { Add } from '@mui/icons-material'
import { Alert, Box, Button, Snackbar, Tab, TablePagination, Tabs, Typography } from '@mui/material'
import axios from 'axios'
import { useContext, useEffect, useMemo, useState } from 'react'
import { dataContext } from '../index'
import NotesDrawer, { type NoteItem } from '../components/notes/NotesDrawer'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { SupplierManufacturersDrawer } from './SupplierManufacturersDrawer'
import { usePagination } from '../components/shared/usePagination'
import { useDrawerState } from '../components/shared/useDrawerState'
import { EditSupplierDrawer } from './EditSupplierDrawer'
import { NewSupplierRequestDialog } from './NewSupplierRequestDialog'
import { SelectNetsuiteVendorDialog } from './SelectNetsuiteVendorDialog'
import { SupplierFilters } from './SupplierFilters'
import { SupplierTable } from './SupplierTable'
import { useManufacturerData } from '../ManufacturerList/useManufacturerData'
import { useSupplierData } from './useSupplierData'
import { useSupplierFilters } from './useSupplierFilters'
import { useTabState } from './useTabState'
import '../components/styles/ListPage.css'
import type { NewSupplierDraft, SupplierRow, SupplierTab } from './SupplierList.types'
import { approveSupplierRow } from './helpers'

export default function SupplierList() {
  const { user } = useContext(dataContext)

  // Set page title
  useEffect(() => {
    document.title = 'Suppliers List | Order Portal'
  }, [])

  // Data loading
  const { rows, setRows, loading, error: loadError } = useSupplierData()
  const { rows: manufacturerRows } = useManufacturerData()

  // Tab state
  const { activeTab, setActiveTab, pendingRows, setPendingRows, activeRows } = useTabState(rows, [])

  // Filters
  const { filters, setFilters, filteredRows, supplierOptions, typeOptions } = useSupplierFilters(activeRows, rows)

  // Pagination
  const {
    page,
    setPage,
    rowsPerPage,
    pagedItems: pagedRows,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination(filteredRows, 25)

  // Drawers using useDrawerState hook
  const notesDrawer = useDrawerState()
  const authorizedOemDrawer = useDrawerState()
  const newSupplierDrawer = useDrawerState()
  const editDrawer = useDrawerState()

  // Row tracking for drawers
  const [activeRowId, setActiveRowId] = useState<number | null>(null)
  const [editRowId, setEditRowId] = useState<number | null>(null)

  // Notes state
  const [notesMap, setNotesMap] = useState<Partial<Record<number, NoteItem[]>>>({})

  // Derived state
  const activeNotes = useMemo(() => {
    if (activeRowId === null) return []
    return notesMap[activeRowId] ?? []
  }, [activeRowId, notesMap])

  const selectedSupplier = useMemo(() => {
    if (editRowId == null) return null
    return [...rows, ...pendingRows].find(r => r.id === editRowId) ?? null
  }, [editRowId, rows, pendingRows])

  const oemOptions = useMemo(() => {
    return manufacturerRows.map(r => r.provider).sort()
  }, [manufacturerRows])

  const activeRowPendingOems = useMemo(() => {
    if (activeRowId === null) return undefined
    const pendingRow = pendingRows.find(r => r.id === activeRowId)
    return pendingRow?.authorizedOems
  }, [activeRowId, pendingRows])

  // Event handlers
  const handleTabChange = (_e: React.SyntheticEvent, value: SupplierTab) => {
    setActiveTab(value)
    setFilters(f => ({ ...f, supplier: '', type: '', blanket: false }))
    setActiveRowId(null)
    notesDrawer.close()
    authorizedOemDrawer.close()
    setPage(0)
  }

  const [approveTarget, setApproveTarget] = useState<SupplierRow | null>(null)
  const [netsuiteDialogOpen, setNetsuiteDialogOpen] = useState(false)

  const handleApproveSupplier = (row: SupplierRow) => {
    setApproveTarget(row)
  }

  const confirmApproveSupplier = () => {
    setNetsuiteDialogOpen(true)
  }

  const handleNetsuiteVendorProceed = async (_vendor: string) => {
    if (!approveTarget) return
    try {
      const { data } = await axios.post<{ id: number }>('/createSupplier', {
        name: approveTarget.name,
        type: approveTarget.type,
        blanket: approveTarget.blanket,
        documentsLabel: approveTarget.documentsLabel || 'Link',
        documentsUrl: approveTarget.documentsUrl || '',
        authorizedOems: approveTarget.authorizedOems,
      })
      const approvedRow = { ...approveTarget, id: data.id }
      setPendingRows(prev => prev.filter(r => r.id !== approveTarget.id))
      setRows(prev => [approvedRow, ...prev])

      const manufacturerIds = approveTarget.authorizedOems
        .map(name => manufacturerRows.find(m => m.provider === name)?.id)
        .filter((id): id is number => id !== undefined)
      if (manufacturerIds.length > 0) {
        await axios.post(`/supplier/${data.id}/manufacturers`, { manufacturerIds })
      }
    } catch (err) {
      console.error('Failed to create supplier in DB', err)
      approveSupplierRow(approveTarget, setPendingRows, setRows)
    }
    setNetsuiteDialogOpen(false)
    setApproveTarget(null)
  }

  const handleNetsuiteVendorCancel = () => {
    setNetsuiteDialogOpen(false)
    setApproveTarget(null)
  }

  const handleAddNewSupplier = (draft: NewSupplierDraft) => {
    const newRow: SupplierRow = {
      id: Date.now(),
      name: draft.name.trim(),
      type: draft.type.trim(),
      blanket: draft.blanket,
      documentsLabel: 'Link',
      documentsUrl: '',
      authorizedOems: draft.requestedOems,
    }

    setPendingRows(prev => [newRow, ...prev])
    setActiveTab('pending')
    setFilters(f => ({ ...f, supplier: '', type: '', blanket: false }))
    setPage(0)
    newSupplierDrawer.close()
  }

  const updateRowBlanket = (id: number, checked: boolean) => {
    if (activeTab === 'approved') {
      setRows(prev => prev.map(r => (r.id === id ? { ...r, blanket: checked } : r)))
    } else {
      setPendingRows(prev => prev.map(r => (r.id === id ? { ...r, blanket: checked } : r)))
    }
  }

  const patchSupplierRow = (id: number, patch: Partial<SupplierRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
    setPendingRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
  }

  const openEditSupplier = (rowId: number) => {
    setEditRowId(rowId)
    editDrawer.open()
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const handleDeleteSupplier = async () => {
    if (!selectedSupplier) return
    const isApproved = rows.some(r => r.id === selectedSupplier.id)
    if (isApproved) {
      try {
        await axios.delete(`/supplier/${selectedSupplier.id}`)
        setRows(prev => prev.filter(r => r.id !== selectedSupplier.id))
        setDeleteConfirmOpen(false)
        editDrawer.close()
        setEditRowId(null)
        setSnackbar({ open: true, message: 'Supplier deleted.', severity: 'success' })
      } catch (err) {
        console.error('Failed to delete supplier', err)
        setDeleteConfirmOpen(false)
        setSnackbar({ open: true, message: 'Failed to delete supplier. Please try again.', severity: 'error' })
      }
    } else {
      setPendingRows(prev => prev.filter(r => r.id !== selectedSupplier.id))
      setDeleteConfirmOpen(false)
      editDrawer.close()
      setEditRowId(null)
      setSnackbar({ open: true, message: 'Supplier deleted.', severity: 'success' })
    }
  }

  return (
    <div className="list-page">
      <SupplierFilters
        filters={filters}
        onFiltersChange={setFilters}
        supplierOptions={supplierOptions}
        typeOptions={typeOptions}
        oemOptions={oemOptions}
      />

      <div className="list-table-container">
        <div className="list-table-pagination-header">
          <div className="list-table-pagination-header-left">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: 0,
                '& .MuiTab-root': { minHeight: 0, textTransform: 'none' },
              }}
            >
              <Tab value="approved" label="Approved Suppliers" />
              <Tab value="pending" label="Suppliers Pending Authorization" />
            </Tabs>

            <Button variant="contained" size="large" startIcon={<Add />} onClick={newSupplierDrawer.open}>
              Add New
            </Button>
          </div>

          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>

        {loading && <Typography sx={{ p: 2 }}>Loading suppliers…</Typography>}
        {loadError && <Typography sx={{ p: 2, color: 'error.main' }}>{loadError}</Typography>}

        <SupplierTable
          rows={pagedRows}
          activeTab={activeTab}
          onEditSupplier={openEditSupplier}
          onOpenAuthorizedOem={rowId => {
            setActiveRowId(rowId)
            authorizedOemDrawer.open()
          }}
          onOpenNotes={rowId => {
            setActiveRowId(rowId)
            notesDrawer.open()
          }}
          onApprove={handleApproveSupplier}
          onBlanketChange={updateRowBlanket}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      </div>

      {/* Drawers and Modals */}
      <SupplierManufacturersDrawer
        open={authorizedOemDrawer.isOpen}
        onClose={() => {
          authorizedOemDrawer.close()
          setActiveRowId(null)
        }}
        supplierId={activeRowId}
        pendingOems={activeRowPendingOems}
      />

      <NotesDrawer
        open={notesDrawer.isOpen}
        onClose={() => {
          notesDrawer.close()
          setActiveRowId(null)
        }}
        header="Notes"
        notes={activeNotes}
        onAddNote={({ notes: noteText }) => {
          if (activeRowId === null) return
          const today = new Date()
          const newNote: NoteItem = {
            id: crypto.randomUUID(),
            dateTime: `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(
              2,
              '0',
            )}/${today.getFullYear()}`,
            user: user.displayName ?? 'Unknown',
            title: 'Update',
            notes: noteText,
          }
          setNotesMap(prev => ({
            ...prev,
            [activeRowId]: [newNote, ...(prev[activeRowId] ?? [])],
          }))
        }}
      />

      <NewSupplierRequestDialog
        open={newSupplierDrawer.isOpen}
        onClose={newSupplierDrawer.close}
        onSubmit={handleAddNewSupplier}
        typeOptions={typeOptions}
        oemOptions={oemOptions}
      />

      <ConfirmDialog
        open={approveTarget !== null && !netsuiteDialogOpen}
        title="Approve Supplier?"
        body="Are you sure you want to approve this supplier?"
        confirmLabel="APPROVE"
        cancelLabel="CANCEL"
        onConfirm={confirmApproveSupplier}
        onCancel={() => setApproveTarget(null)}
      />

      <SelectNetsuiteVendorDialog
        open={netsuiteDialogOpen}
        onCancel={handleNetsuiteVendorCancel}
        onProceed={handleNetsuiteVendorProceed}
      />

      <EditSupplierDrawer
        open={editDrawer.isOpen}
        onClose={() => {
          editDrawer.close()
          setEditRowId(null)
        }}
        supplier={selectedSupplier}
        typeOptions={typeOptions}
        onSave={async (patch: Partial<SupplierRow>) => {
          if (!selectedSupplier) return
          const isApproved = rows.some(r => r.id === selectedSupplier.id)
          if (isApproved) {
            try {
              await axios.post(`/updateSupplier/${selectedSupplier.id}`, {
                name: patch.name ?? selectedSupplier.name,
                type: patch.type ?? selectedSupplier.type,
                blanket: patch.blanket ?? selectedSupplier.blanket,
                documentsUrl: patch.documentsUrl ?? selectedSupplier.documentsUrl,
              })
            } catch (err) {
              console.error('Failed to update supplier', err)
            }
          }
          patchSupplierRow(selectedSupplier.id, patch)
          editDrawer.close()
          setEditRowId(null)
        }}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Supplier?"
        body="Are you sure you want to delete this supplier? This will also remove it from any linked manufacturers."
        confirmLabel="DELETE"
        cancelLabel="CANCEL"
        onConfirm={handleDeleteSupplier}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <Snackbar open={snackbar.open}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}
